import { computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import store from '@state/store';
import { getCoreAxiosInstance } from '@utils/axios';
import { queryClient as appClient } from './client';

const api = getCoreAxiosInstance();

export const chatKeys = {
  recent: () => ['chat', 'recent'],
};

const toRecentChats = ({ recentChats, unreadCount }) => ({
  unreadCount,
  chats: recentChats.map((recent) => ({
    user: recent.user,
    userId: recent.userId,
    text: recent.lastMessage,
    isUnread: parseInt(recent.lastMessageId) > parseInt(recent.lastReadMessageId),
  })),
});

// Stale at once, so a tab shown again refetches straight away.
export const recentChatsQuery = () => ({
  queryKey: chatKeys.recent(),
  queryFn: () => api.get(`${process.env.VUE_APP_MOBILE_API}/chat/recent-chats`).then((res) => toRecentChats(res.data)),
  staleTime: 0,
});

// Every observer runs its own timer, so only the top bar's polls.
const POLL_EVERY = 10000;

// Opening a chat reads it; the API records that when the chat loads.
export const showChatRead = (userId, client = appClient) =>
  client.setQueryData(chatKeys.recent(), (data) => {
    const chat = data?.chats.find((x) => x.userId === userId);
    if (!chat?.isUnread) return data;
    return {
      unreadCount: Math.max(data.unreadCount - 1, 0),
      chats: data.chats.map((x) => (x === chat ? { ...x, isUnread: false } : x)),
    };
  });

// `chats` is null until the first answer, [] if it failed. `poll` refetches
// every 10s while the tab is visible; vue-query skips hidden tabs.
export const useRecentChats = ({ poll = false } = {}) => {
  const { data, isError } = useQuery(() => ({
    ...recentChatsQuery(),
    refetchInterval: poll && POLL_EVERY,
    enabled: !!store.state.auth.mobileToken && !store.getters['auth/isStudent'] && !process.env.VUE_APP_IS_LOCAL_INSTALLATION,
  }));
  return {
    chats: computed(() => data.value?.chats ?? (isError.value ? [] : null)),
    unreadCount: computed(() => data.value?.unreadCount ?? 0),
  };
};
